import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { 
    ArrowLeft, Send, Paperclip, MoreVertical, 
    Users, Lock, Hash, ChefHat, Wine, Building2,
    Clock, CheckCircle2, AlertCircle
} from 'lucide-react';

/**
 * Mobile Conversation View
 * Full-screen conversation view for mobile devices
 * Follows iOS/Android messaging app patterns
 */
const MobileConversationView = ({
    conversation,
    messages = [],
    currentUserId,
    onBack,
    onSendMessage,
    onLoadMore,
    sending = false,
    hasMore = false,
    className
}) => {
    const [messageText, setMessageText] = useState('');
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    
    // Scroll to bottom on new messages
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);
    
    const handleSend = () => {
        if (!messageText.trim() || sending) return;
        onSendMessage(messageText.trim());
        setMessageText('');
    };
    
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };
    
    // Get conversation icon based on type
    const getConversationIcon = () => {
        if (!conversation) return <Users className="w-5 h-5" />;
        
        const icons = {
            'Cuisine': <ChefHat className="w-5 h-5 text-orange-500" />,
            'Bar': <Wine className="w-5 h-5 text-purple-500" />,
            'Direction': <Building2 className="w-5 h-5 text-blue-500" />,
        };
        
        const name = conversation.name || '';
        for (const [key, icon] of Object.entries(icons)) {
            if (name.includes(key)) return icon;
        }
        
        if (conversation.is_private) {
            return <Lock className="w-4 h-4 text-muted-foreground" />;
        }
        return <Hash className="w-4 h-4 text-muted-foreground" />;
    };
    
    // Format message time
    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };
    
    // Format message date for separators
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) {
            return "Aujourd'hui";
        } else if (date.toDateString() === yesterday.toDateString()) {
            return "Hier";
        }
        return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    };
    
    // Group messages by date
    const groupedMessages = messages.reduce((groups, message) => {
        const date = formatDate(message.created_at);
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(message);
        return groups;
    }, {});
    
    if (!conversation) {
        return null;
    }
    
    return (
        <div className={cn(
            "mobile-fullscreen-view",
            className
        )} data-testid="mobile-conversation-view">
            {/* Header */}
            <div className="mobile-fullscreen-header">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onBack}
                    className="mobile-btn-icon shrink-0"
                    data-testid="conversation-back-btn"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Avatar className="w-10 h-10 shrink-0">
                        <AvatarFallback className="bg-primary/10 text-primary">
                            {getConversationIcon()}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-semibold text-base truncate">
                            {conversation.name || 'Conversation'}
                        </h2>
                        {conversation.member_names && (
                            <p className="text-xs text-muted-foreground truncate">
                                {conversation.member_names.slice(0, 3).join(', ')}
                                {conversation.member_names.length > 3 && ` +${conversation.member_names.length - 3}`}
                            </p>
                        )}
                    </div>
                </div>
                
                <Button
                    variant="ghost"
                    size="icon"
                    className="mobile-btn-icon shrink-0"
                >
                    <MoreVertical className="w-5 h-5" />
                </Button>
            </div>
            
            {/* Messages */}
            <div 
                ref={scrollRef}
                className="mobile-fullscreen-content bg-muted/30 px-4 py-2"
            >
                {hasMore && (
                    <div className="text-center py-4">
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={onLoadMore}
                        >
                            Charger plus
                        </Button>
                    </div>
                )}
                
                {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                    <div key={date}>
                        {/* Date separator */}
                        <div className="flex items-center justify-center my-4">
                            <span className="px-3 py-1 rounded-full bg-muted text-xs text-muted-foreground">
                                {date}
                            </span>
                        </div>
                        
                        {/* Messages for this date */}
                        {dateMessages.map((message, idx) => {
                            const isOwn = message.sender_id === currentUserId;
                            const showAvatar = !isOwn && (idx === 0 || dateMessages[idx - 1]?.sender_id !== message.sender_id);
                            
                            return (
                                <div
                                    key={message.id || idx}
                                    className={cn(
                                        "flex mb-2",
                                        isOwn ? "justify-end" : "justify-start"
                                    )}
                                >
                                    {!isOwn && showAvatar && (
                                        <Avatar className="w-8 h-8 mr-2 shrink-0">
                                            <AvatarFallback className="text-xs bg-primary/10">
                                                {message.sender_name?.charAt(0) || '?'}
                                            </AvatarFallback>
                                        </Avatar>
                                    )}
                                    {!isOwn && !showAvatar && <div className="w-8 mr-2" />}
                                    
                                    <div className={cn(
                                        "max-w-[80%] rounded-2xl px-4 py-2",
                                        isOwn 
                                            ? "bg-primary text-primary-foreground rounded-br-md" 
                                            : "bg-card border rounded-bl-md"
                                    )}>
                                        {!isOwn && showAvatar && (
                                            <p className="text-xs font-medium text-primary mb-1">
                                                {message.sender_name}
                                            </p>
                                        )}
                                        <p className="text-sm whitespace-pre-wrap break-words">
                                            {message.content}
                                        </p>
                                        <div className={cn(
                                            "flex items-center gap-1 mt-1",
                                            isOwn ? "justify-end" : "justify-start"
                                        )}>
                                            <span className={cn(
                                                "text-[10px]",
                                                isOwn ? "text-primary-foreground/70" : "text-muted-foreground"
                                            )}>
                                                {formatTime(message.created_at)}
                                            </span>
                                            {isOwn && message.read && (
                                                <CheckCircle2 className="w-3 h-3 text-primary-foreground/70" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
                
                {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                            {getConversationIcon()}
                        </div>
                        <p className="text-muted-foreground">
                            Aucun message
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                            Envoyez le premier message !
                        </p>
                    </div>
                )}
            </div>
            
            {/* Input */}
            <div className="mobile-fullscreen-footer">
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 h-10 w-10"
                    >
                        <Paperclip className="w-5 h-5 text-muted-foreground" />
                    </Button>
                    
                    <Input
                        ref={inputRef}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Message..."
                        className="flex-1 h-10 rounded-full bg-muted border-0"
                        disabled={sending}
                    />
                    
                    <Button
                        onClick={handleSend}
                        disabled={!messageText.trim() || sending}
                        size="icon"
                        className="shrink-0 h-10 w-10 rounded-full"
                    >
                        <Send className="w-5 h-5" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MobileConversationView;
